import { call, fork, put, select } from 'redux-saga/effects';

import { setReviewPage, resetSelectedSku, setTileTypeOverride } from '../actions/productActions';
import { clickstreamAction } from '../../../core/js/actions/analyticsActions';

import { debug } from '../../../core/js/utils/logger';
import eventUtils from '../../../core/js/utils/eventUtils';
import productUtils from '../utils/utils';
import { getQueryParams } from '../../../core/js/utils/routerUtils';
import { getRRTileTemplate, getPlacementIdForPDP } from '../../../core/js/utils/rrUtils';

import { initialSaga } from '../../../core/js/sagas/initialSagas';
import { handleDefaultUrbnStoreSaga } from './productBopisSagas';
import { getProductsRelatedByColorSaga } from './shopTheLookSagas';
import { initializeDynamicYieldSaga } from '../../../core/js/sagas/recommendationsSagas';
import { productModalHashLauncherSaga } from '../sagas/productModalHashLauncher';
import Cookies from '@core/js/services/cookies';
import { AB_PDPTILETYPE } from '@core/js/abTestingConstants';
import { getTileTypeOverride } from '@core/js/utils/abTests';

/**
 * Fired when an PDP is available and viewed by the user
 * @param {Object} product Product visited by the user
 */
export function* pdpPageView(product) {
    const pageView = {
        page: "PDP",
        pageId: _.get(product, 'styleNumber', '')
    };
    yield put(clickstreamAction(pageView));
}

export function* pdpAndAvailable() {
    // Store of the hash since product selection sagas monkey with the url
    const hash = window.location.hash;

    yield fork(handleDefaultUrbnStoreSaga);

    let state = yield select();
    let product = state.product;

    // original query params may be stale if the color/size is OOS
    // so update them to what we've preselected
    // see UW-13727
    const queryParams = yield call(getQueryParams, window.location.href);

    // UW-14642 - Prevent PDP from resetting if query contains a campain id source
    if (!_.isEmpty(queryParams) && !_.has(queryParams, 'bvSource')) {
        const controls = product.productControls;

        if (queryParams.color !== controls.colorCode || controls.backInStockEligible) {
            yield put(resetSelectedSku());
        }
    }

    yield fork(
        getProductsRelatedByColorSaga,
        productUtils.getSelectedColorCode(state),
        productUtils.getInventoryPool()
    );

    yield put(setReviewPage(product.productReviews.reviewPage));

    // get tileType from settings, else global rrRecs config, else fall back to TITLE_ONLY
    const tileCount = _.get(state, 'core.constants.thirdParty.default.rich_relevance.max_tile_count', 6);
    const tileType = getRRTileTemplate(state, 'core.constants.pdpOptions.recommendations_tile_type');
    const placementId = getPlacementIdForPDP(state);

    // TODO: UW-14895 - remove when SS_PDPTILETYPE test is over
    const tileTypeOverride = getTileTypeOverride(Cookies, AB_PDPTILETYPE);
    yield put(setTileTypeOverride(tileTypeOverride));

    yield call(initializeDynamicYieldSaga, placementId, tileCount, tileType);

    state = yield select();
    product = state.product;

    eventUtils.attachProductTileHover(state);

    yield call(productModalHashLauncherSaga, hash);
    yield call(pdpPageView, product);
}

export function* pdpAndUnavailable() {
    let state = yield select();
    const tileCount = _.get(state, 'core.constants.thirdParty.default.rich_relevance.max_tile_count', 6);
    const placementId = _.get(
        state,
        'core.constants.thirdParty.default.dynamic_yield.placement_ids.productSoldOut',
        'pdp'
    );

    yield call(initializeDynamicYieldSaga, placementId, tileCount);

    state = yield select();
    eventUtils.attachProductTileHover(state);
}

/**
 * Saga for initializing the product feature
 */
export function* productInitialSaga() {
    debug('Inside Product Initial Saga');

    yield call(initialSaga);

    const state = yield select();
    const product = state.product;

    if (!product.productUnavailable) {
        // Run when on a pdp that is in stock or out of stock
        yield call(pdpAndAvailable);
    } else {
        // Run when on a pdp that is out of stock
        yield call(pdpAndUnavailable);
    }
}

export default [
    productInitialSaga
];
